'use client';

import { documentToReactComponents } from '@contentful/rich-text-react-renderer';
import { BLOCKS, Document, INLINES, Node, Block, MARKS } from '@contentful/rich-text-types';
import renderEmbeddedEntry from 'components/nodes/EmbeddedEntry';
import renderEntryHyperlink from 'components/nodes/EntryHyperlink';
import renderEmbeddedAsset from 'components/nodes/EmbeddedAsset';
import ResponsiveTable from 'components/nodes/ResponsiveTable';
import Link from 'components/shared/Link/Link';
import { RichTextCopyProps } from 'libs/contentful/props';
import React from 'react';
import { getCleanDocument, getIdFromText, getTextFromChildren } from 'utils/formatters';
import { replaceDynamicTokens } from 'utils/dynamicTokens';
import { cn } from 'utils/styles';
import { renderSuperscript } from 'utils/superscriptUtils';
import { useDisclosureNumberingMap } from 'contexts/DisclosureProvider';
import { BulletListStyle } from 'types/constants';
import { getUnorderedListClasses, getOrderedListClasses, renderListItem } from 'utils/bulletListHelpers';

/**
 * Helper function to get default dynamic tokens.
 * @returns Default dynamic copy tokens including current date.
 */
const getDefaultDynamicTokens = (): Record<string, string> => ({
  currentDate: new Date().toLocaleDateString('en-US', {
    month: 'long',
    day: 'numeric',
    year: 'numeric',
  }),
  // Add more default tokens here as needed
});

const RichTextCopy = ({
  copy,
  additionalOptions,
  ySpacing,
  arrowStyles,
  inline,
  showSimpleListStyle,
  bulletListStyle = BulletListStyle.Arrow,
}: RichTextCopyProps) => {
  // Use the disclosure numbering map from context
  const disclosureNumberingMap = useDisclosureNumberingMap();
  if (!copy) return null;

  // Copy may be a Document or a JSON object
  let cleanCopy = getCleanDocument(copy) as Document;

  // Process any dynamic tokens in the copy
  cleanCopy = replaceDynamicTokens(cleanCopy, getDefaultDynamicTokens());

  // Set the ySpacing classes based on the ySpacing prop, with default having space between elements
  const ySpacingMap = {
    default: 'my-4',
    none: 'my-0',
    sm: 'my-2',
    lg: 'my-4 sm:my-11',
  };

  const ySpacingParagraphMap = {
    default: 'peer-[.paragraph]:my-4',
    none: 'peer-[.paragraph]:my-0',
    sm: 'peer-[.paragraph]:my-2',
    lg: 'peer-[.paragraph]:my-11',
  };

  const ySpacingClass = ySpacingMap[ySpacing || 'default'];
  const ySpacingParagraphClass = ySpacingParagraphMap[ySpacing || 'default'];

  // Set the base classes for the heading and list elements
  const baseHeadingClasses = cn('font-sans font-semibold', ySpacingClass);

  const createHeading = (level: number, classes: string) => {
    const HeadingComponent = (_node: Node, children: React.ReactNode) => {
      const headingText = getTextFromChildren(children);
      const Tag = `h${level}` as keyof JSX.IntrinsicElements;

      return (
        <Tag className={cn(baseHeadingClasses, classes)} id={getIdFromText(headingText)}>
          {children}
        </Tag>
      );
    };

    HeadingComponent.displayName = `Heading${level}`;
    return HeadingComponent;
  };

  return documentToReactComponents(cleanCopy, {
    renderNode: {
      [BLOCKS.HEADING_2]: createHeading(2, 'text-heading-md my-11'),
      [BLOCKS.HEADING_3]: createHeading(3, 'text-heading-sm'),
      [BLOCKS.HEADING_4]: createHeading(4, 'font-semibold'),
      [BLOCKS.HEADING_5]: createHeading(5, 'font-semibold'),
      [BLOCKS.HEADING_6]: createHeading(6, 'font-semibold'),
      [BLOCKS.QUOTE]: (node: Node, children: React.ReactNode) => (
        <blockquote className={`font-normal text-heading-lg ${ySpacingClass}`}>{children}</blockquote>
      ),
      [BLOCKS.HR]: () => <hr className={`${ySpacingClass} border-content-primary`} />,
      [BLOCKS.UL_LIST]: (node: Node, children: React.ReactNode) => {
        return <ul className={getUnorderedListClasses(showSimpleListStyle, bulletListStyle)}>{children}</ul>;
      },
      [BLOCKS.OL_LIST]: (node: Node, children: React.ReactNode) => (
        <ol className={getOrderedListClasses(showSimpleListStyle)}>{children}</ol>
      ),
      [BLOCKS.LIST_ITEM]: (node: Node, children: React.ReactNode) =>
        renderListItem(children, showSimpleListStyle, arrowStyles, bulletListStyle),
      [BLOCKS.PARAGRAPH]: (node: Node, children: React.ReactNode) => (
        <p className={cn('peer paragraph', ySpacingParagraphClass, 'last:mb-0', inline && 'inline m-0')}>{children}</p>
      ),
      [BLOCKS.TABLE]: (node: Node, children: React.ReactNode) => (
        <ResponsiveTable node={node as Block}>
          <tbody>{children}</tbody>
        </ResponsiveTable>
      ),
      [BLOCKS.TABLE_ROW]: (node: Node, children: React.ReactNode) => (
        <tr className={`border-b border-pebble`}>{children}</tr>
      ),
      [BLOCKS.TABLE_HEADER_CELL]: (node: Node, children: React.ReactNode) => (
        <th className={`font-sans font-semibold py-6 w-100`}>{children}</th>
      ),
      [BLOCKS.TABLE_CELL]: (node: Node, children: React.ReactNode) => (
        <td className={`py-6 w-100 text-(--table-text-color)`}>{children}</td>
      ),
      [BLOCKS.EMBEDDED_ASSET]: (node: Node, children: React.ReactNode) => {
        return <div className={`${ySpacingClass}`}>{renderEmbeddedAsset(node, children, copy)}</div>;
      },
      [INLINES.HYPERLINK]: (node: Node, children: React.ReactNode) => {
        return (
          <Link href={node.data.uri} className={cn('inline underline')} data-testid="inline-hyperlink">
            {children}
          </Link>
        );
      },
      [INLINES.ENTRY_HYPERLINK]: (node: Node, children: React.ReactNode) => renderEntryHyperlink(node, children, copy),
      [INLINES.EMBEDDED_ENTRY]: (node: Node, children: React.ReactNode) =>
        renderEmbeddedEntry(node, children, copy, disclosureNumberingMap),
    },
    renderMark: {
      [MARKS.SUPERSCRIPT]: (children: React.ReactNode) => renderSuperscript(children),
    },
    renderText: text => {
      return text.split('\n').reduce<React.ReactNode[]>((children, textSegment, index) => {
        return [...children, index > 0 && <br key={index} />, textSegment];
      }, []);
    },
    ...additionalOptions,
  });
};

export default RichTextCopy;
